import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react';
import { cn } from './cn';

type ToastKind = 'success' | 'error' | 'warning' | 'info';
interface ToastItem {
  id: number;
  kind: ToastKind;
  title: string;
  description?: string;
}

interface ToastContextValue {
  push: (toast: Omit<ToastItem, 'id'>) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const KIND_CFG: Record<
  ToastKind,
  { icon: ReactNode; color: string; bg: string; border: string }
> = {
  success: {
    icon: <CheckCircle2 className="size-5" />,
    color: '#1D9E75',
    bg: 'rgba(29, 158, 117, 0.13)',
    border: 'rgba(29, 158, 117, 0.28)',
  },
  warning: {
    icon: <AlertTriangle className="size-5" />,
    color: '#EF9F27',
    bg: 'rgba(239, 159, 39, 0.13)',
    border: 'rgba(239, 159, 39, 0.28)',
  },
  error: {
    icon: <XCircle className="size-5" />,
    color: '#E24B4A',
    bg: 'rgba(226, 75, 74, 0.13)',
    border: 'rgba(226, 75, 74, 0.28)',
  },
  info: {
    icon: <Info className="size-5" />,
    color: '#378ADD',
    bg: 'rgba(55, 138, 221, 0.13)',
    border: 'rgba(55, 138, 221, 0.28)',
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const counter = useRef(0);

  const push = useCallback((t: Omit<ToastItem, 'id'>) => {
    const id = ++counter.current;
    setToasts((prev) => [...prev, { ...t, id }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, 4500);
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      push,
      success: (title, description) => push({ kind: 'success', title, description }),
      error: (title, description) => push({ kind: 'error', title, description }),
      warning: (title, description) => push({ kind: 'warning', title, description }),
      info: (title, description) => push({ kind: 'info', title, description }),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          className="fixed top-2 left-2 right-2 sm:top-4 sm:right-4 sm:left-auto z-[60] flex flex-col gap-2 w-auto sm:w-full sm:max-w-sm pointer-events-none"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          {toasts.map((t) => {
            const cfg = KIND_CFG[t.kind];
            return (
              <div
                key={t.id}
                className={cn(
                  'pointer-events-auto relative flex items-start gap-3 rounded-xl shadow-popover p-4 animate-fade-in',
                )}
                style={{
                  background: cfg.bg,
                  border: `1px solid ${cfg.border}`,
                  backdropFilter: 'blur(12px)',
                }}
              >
                <span style={{ color: cfg.color }} className="shrink-0 mt-0.5">
                  {cfg.icon}
                </span>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-sm font-semibold leading-snug"
                    style={{ color: cfg.color }}
                  >
                    {t.title}
                  </div>
                  {t.description && (
                    <div className="text-xs text-ink-muted mt-0.5 leading-relaxed">
                      {t.description}
                    </div>
                  )}
                </div>
                <button
                  onClick={() =>
                    setToasts((prev) => prev.filter((x) => x.id !== t.id))
                  }
                  className="shrink-0 p-1 rounded-md transition-colors text-ink-subtle hover:text-ink"
                  aria-label="Fechar"
                  type="button"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast deve ser usado dentro de ToastProvider');
  return ctx;
}
